import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOCALS } from '@constants/config';
import { STATUS_CODE } from '@constants/api';
import { SWITCH_ROUTES } from '@constants/routes';
import { parseSeatGame, visibleRegions, buildIpg } from '@constants/seatGroups';
import { getSeats, getSeatHis, getCasinoPlay, exitSeat } from '@api/casino';
import { useGlobalProps } from '@hooks/useGlobalProps';

const SEATS_PER_PAGE = 20;

/**
 * CasinoSeatModal component
 * Modal for selecting casino seats
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.game - 原始遊戲陣列（12 欄入座制或 6 欄舊格式）
 * @param {string} [props.groupKey] - JP/Fish/Slot/Mult；null 或 legacy 資料＝無區域模式（現行為）
 * @param {string} [props.initialRegionKey] - 返回遊戲時指定起始區；無效則取第一個有座區
 * @param {number} [props.initialPage] - 回到原座位時的起始頁（預設 1）
 */
export default function CasinoSeatModal({ show, onClose, game, groupKey, initialRegionKey, initialPage }) {
    const { t, i18n } = useTranslation();
    const tCommon = t('common', { returnObjects: true });
    const navigate = useNavigate();
    const { user, balance } = useGlobalProps();

    const [loading, setLoading] = useState(false);
    const [seats, setSeats] = useState([]);
    const [totalSeats, setTotalSeats] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [historyPopup, setHistoryPopup] = useState(null); // { cacc, data }
    const [historyLoading, setHistoryLoading] = useState(false);
    const [enteringGame, setEnteringGame] = useState(false);
    const [exitingGame, setExitingGame] = useState(false);
    // 被點擊的已佔用座位號碼，用於顯示列內提示（2 秒後自動清除）
    const [occupiedSeatNo, setOccupiedSeatNo] = useState(null);
    const [regionKey, setRegionKey] = useState(null);
    // 入座/離座失敗訊息：改站內顯示，原生 alert 在 App WebView 會卡住頁面
    const [actionError, setActionError] = useState(null);
    // 待確認的座位：點座位先出確認框（座位號＋遊戲名），確認後才進遊戲
    const [pendingSeat, setPendingSeat] = useState(null);
    const requestKeyRef = useRef('');

    const gameIndex = game?.[0]; // A, B, C...
    const parsed = parseSeatGame(game);
    const regionMode = !!groupKey && !!parsed && !parsed.legacy;
    const regions = regionMode ? visibleRegions(parsed) : [];
    // 座位表文字/格線跟隨當前區色，幫助玩家辨識所在區（客戶 2026-10-12 要求；底色不動）
    const activeRegionColor = regionMode
        ? (regions.find((r) => r.key === regionKey)?.color ?? null)
        : null;
    const nameEN = game?.[2];
    const nameCN = game?.[3];
    const isChinese = i18n.language === LOCALS.cn;
    const displayName = isChinese ? nameCN : nameEN;

    const totalPages = Math.ceil(totalSeats / SEATS_PER_PAGE);

    // Fetch seats when modal opens, page or region changes
    const fetchSeats = useCallback(async () => {
        if (!show || !gameIndex || !user?.ukey) return;
        // 四區全 0（regionKey null）不打 API；regionKey 殘留自上一個遊戲且
        // 不在本遊戲的有效區域時也不打，避免開彈窗瞬間先送一筆舊區請求
        if (regionMode && (!regionKey || !regions.some((r) => r.key === regionKey))) return;

        const ipg = regionMode
            ? buildIpg(groupKey, regionKey, gameIndex, currentPage)
            : `${gameIndex}${currentPage}`;
        if (!ipg) return;

        // 過期回應防護：只接受「當前 群組/遊戲/區域/頁碼」的回應，
        // 避免快速切區時舊區座位蓋到新區（點擊會進錯區）
        const requestKey = `${groupKey || ''}|${gameIndex}|${regionKey || ''}|${currentPage}`;
        requestKeyRef.current = requestKey;

        setLoading(true);
        try {
            const res = await getSeats({
                pgc: SEATS_PER_PAGE,
                ipg,
                ukey: user.ukey,
            });

            if (requestKeyRef.current !== requestKey) return;
            if (res.rtn === STATUS_CODE.SUCCESS) {
                setSeats(res.seat_list || []);
                setTotalSeats(res.ttl_seats || 0);
            } else {
                console.error('getSeats error:', res.msg);
                setSeats([]);
            }
        } catch (error) {
            if (requestKeyRef.current !== requestKey) return;
            console.error('getSeats error:', error);
            setSeats([]);
        } finally {
            if (requestKeyRef.current === requestKey) setLoading(false);
        }
        // regions 由 game 推導、每次 render 新陣列，放入 deps 會無限重抓；
        // 其變動已由 gameIndex + groupKey 涵蓋
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, gameIndex, currentPage, user?.ukey, regionMode, regionKey, groupKey]);

    useEffect(() => {
        if (show) {
            const valid = regions.find((r) => r.key === initialRegionKey);
            setRegionKey(valid ? valid.key : (regions[0]?.key ?? null));
            // 回到原座位：指定了有效起始區才尊重起始頁，否則回第 1 頁
            setCurrentPage(valid && initialPage > 0 ? initialPage : 1);
            setSeats([]);
            setHistoryPopup(null);
            setOccupiedSeatNo(null);
            setActionError(null);
            setPendingSeat(null);
        }
        // regions 由 game 推導，依 gameIndex 變化即可
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, gameIndex, initialRegionKey, initialPage]);

    useEffect(() => {
        fetchSeats();
    }, [fetchSeats]);

    // 換頁時清除提示（座位號碼會重複，避免殘留在錯誤的列）
    useEffect(() => {
        setOccupiedSeatNo(null);
    }, [currentPage]);

    // 提示 2 秒後自動消失；連續點擊時 cleanup 會取消舊計時器
    useEffect(() => {
        if (occupiedSeatNo === null) return;
        const timer = setTimeout(() => setOccupiedSeatNo(null), 2000);
        return () => clearTimeout(timer);
    }, [occupiedSeatNo]);

    // 入座/離座錯誤訊息 3 秒後自動消失
    useEffect(() => {
        if (actionError === null) return;
        const timer = setTimeout(() => setActionError(null), 3000);
        return () => clearTimeout(timer);
    }, [actionError]);

    // 座位表載入後，若自己的座位在本頁，自動捲到該列（回到原座位）
    const mySeatRowRef = useRef(null);
    useEffect(() => {
        if (!loading && mySeatRowRef.current &&
            typeof mySeatRowRef.current.scrollIntoView === 'function') {
            mySeatRowRef.current.scrollIntoView({ block: 'center' });
        }
    }, [loading, seats]);

    // Handle history click
    const handleHistoryClick = async (cacc, e) => {
        e.stopPropagation();
        if (!cacc || !user?.ukey) return;

        setHistoryLoading(true);
        setHistoryPopup({ cacc, data: null });

        try {
            const res = await getSeatHis({ cacc, ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS) {
                setHistoryPopup({ cacc, data: res.seat_his });
            } else {
                console.error('getSeatHis error:', res.msg);
                setHistoryPopup(null);
            }
        } catch (error) {
            console.error('getSeatHis error:', error);
            setHistoryPopup(null);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Handle seat click：先出確認框，確認後才進遊戲（客戶 2026-10-08 要求）
    const handleSeatClick = (seat) => {
        const cacc = seat[1]; // casino account
        const seatNo = seat[0];
        const seatUsername = seat[2];
        if (!cacc || !user?.ukey || enteringGame) return;

        // 已被其他帳號佔用的座位：直接顯示提示，不呼叫 API
        const isEmpty = !seatUsername;
        const isMyself = seatUsername === user?.acc;
        if (!isEmpty && !isMyself) {
            setOccupiedSeatNo(seatNo);
            return;
        }

        setPendingSeat(seat);
    };

    // 確認後實際入座進遊戲
    const enterSeat = async (seat) => {
        const cacc = seat[1];
        if (!cacc || !user?.ukey || enteringGame) return;

        setEnteringGame(true);
        try {
            const res = await getCasinoPlay({ cacc, ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS && res.play_url) {
                onClose();
                navigate(SWITCH_ROUTES.GAME, {
                    state: {
                        gameUrl: res.play_url,
                        isCasino: true,
                        casinoGame: game, // Pass game data for reopening modal
                        casinoGroupKey: groupKey,
                        casinoRegionKey: regionKey,
                    },
                });
            } else {
                console.error('getCasinoPlay error:', res.msg);
                setActionError(res.msg || 'Failed to enter game');
            }
        } catch (error) {
            console.error('getCasinoPlay error:', error);
            setActionError('Failed to enter game');
        } finally {
            setEnteringGame(false);
        }
    };

    // Handle exit seat
    const handleExitSeat = async (e) => {
        e.stopPropagation();
        if (!user?.ukey || exitingGame) return;

        setExitingGame(true);
        try {
            const res = await exitSeat({ ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS) {
                // Close modal after successful exit
                onClose();
            } else {
                console.error('exitSeat error:', res.msg);
                setActionError(res.msg || 'Failed to exit seat');
            }
        } catch (error) {
            console.error('exitSeat error:', error);
            setActionError('Failed to exit seat');
        } finally {
            setExitingGame(false);
        }
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // 換區：重置頁碼並清空舊區資料，避免載入期間殘留可點擊的舊座位
    const handleRegionClick = (key) => {
        if (key === regionKey) return;
        setRegionKey(key);
        setCurrentPage(1);
        setSeats([]);
        setHistoryPopup(null);
        setOccupiedSeatNo(null);
        setActionError(null);
        setPendingSeat(null);
    };

    if (!show) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    // User info for display
    const username = user?.acc || '';
    const displayBalance = balance?.pts ?? 0;

    const modalContent = (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderLeft>
                        <h3>{displayName}</h3>
                        <UserInfo>
                            <span className="balance-label">Balance:</span>
                            <span className="balance">{displayBalance} Cr.</span>
                            <span className="username">{username}</span>
                        </UserInfo>
                    </HeaderLeft>
                    <CloseIcon onClick={onClose} />
                </ModalHeader>

                {regionMode && regions.length > 0 && (
                    <RegionTabs>
                        {regions.map((r) => (
                            <RegionTab
                                key={r.key}
                                $color={r.color}
                                $active={r.key === regionKey}
                                onClick={() => handleRegionClick(r.key)}
                            >
                                {r.label}
                            </RegionTab>
                        ))}
                    </RegionTabs>
                )}

                <ModalBody>
                    {actionError && <ActionError>{actionError}</ActionError>}
                    {regionMode && regions.length === 0 ? (
                        <NoSeats>{tCommon['casino@no_seats']}</NoSeats>
                    ) : loading ? (
                        <LoadingWrapper>
                            <img src="/images/shared/loading.gif" alt="loading" />
                        </LoadingWrapper>
                    ) : (
                        <>
                            <SeatTable $color={activeRegionColor}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{tCommon['casino@col_no']}</th>
                                        <th>{tCommon['casino@col_user']}</th>
                                        <th>{tCommon['casino@col_tracker']}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {seats.map((seat, idx) => {
                                        const [seatNo, cacc, seatUsername, history] = seat;
                                        const isEmpty = !seatUsername;
                                        const isMyself = seatUsername === username;
                                        return (
                                            <React.Fragment key={cacc || idx}>
                                                <SeatRow
                                                    ref={isMyself ? mySeatRowRef : null}
                                                    $isEmpty={isEmpty}
                                                    $isMyself={isMyself}
                                                    onClick={() => handleSeatClick(seat)}
                                                >
                                                    <td className="action-cell">
                                                        {isMyself && (
                                                            <ExitButton
                                                                onClick={handleExitSeat}
                                                                disabled={exitingGame}
                                                                title="Exit Seat"
                                                            >
                                                                {exitingGame ? '...' : <ExitIcon />}
                                                            </ExitButton>
                                                        )}
                                                    </td>
                                                    <td>{seatNo}</td>
                                                    <td>{seatUsername || '-'}</td>
                                                    <td>
                                                        {history ? (
                                                            <HistoryLink
                                                                onClick={(e) => handleHistoryClick(cacc, e)}
                                                            >
                                                                {history}
                                                            </HistoryLink>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                </SeatRow>
                                                {occupiedSeatNo === seatNo && (
                                                    <OccupiedRow>
                                                        <td colSpan={4}>
                                                            {tCommon['casino@seat_occupied']}
                                                        </td>
                                                    </OccupiedRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </SeatTable>

                            {totalPages > 1 && (
                                <Pagination>
                                    <PageButton
                                        onClick={() => goToPage(1)}
                                        disabled={currentPage === 1}
                                    >
                                        &laquo;
                                    </PageButton>
                                    <PageButton
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        &lsaquo;
                                    </PageButton>
                                    <PageInfo>
                                        {currentPage} / {totalPages}
                                    </PageInfo>
                                    <PageButton
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        &rsaquo;
                                    </PageButton>
                                    <PageButton
                                        onClick={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    >
                                        &raquo;
                                    </PageButton>
                                </Pagination>
                            )}
                        </>
                    )}
                </ModalBody>

                {/* 入座確認框：座位號＋遊戲名，確認才進遊戲 */}
                {pendingSeat && (
                    <ConfirmOverlay onClick={() => setPendingSeat(null)}>
                        <ConfirmBox onClick={(e) => e.stopPropagation()}>
                            <p className="confirm-title">{tCommon['casino@confirm_enter']}</p>
                            <p className="confirm-seat">{tCommon['casino@col_no']} {pendingSeat[0]}</p>
                            <p className="confirm-game">{displayName}</p>
                            <div className="confirm-btns">
                                <button
                                    className="yes"
                                    onClick={() => {
                                        const seat = pendingSeat;
                                        setPendingSeat(null);
                                        enterSeat(seat);
                                    }}
                                >
                                    {tCommon['common@confirm']}
                                </button>
                                <button className="no" onClick={() => setPendingSeat(null)}>
                                    {tCommon['common@cancel']}
                                </button>
                            </div>
                        </ConfirmBox>
                    </ConfirmOverlay>
                )}

                {/* History Popup */}
                {historyPopup && (
                    <HistoryPopup onClick={() => setHistoryPopup(null)}>
                        <HistoryContent onClick={(e) => e.stopPropagation()}>
                            <HistoryHeader>
                                <span>{tCommon['casino@history_title']}</span>
                                <CloseIcon onClick={() => setHistoryPopup(null)} />
                            </HistoryHeader>
                            {historyLoading ? (
                                <LoadingWrapper>
                                    <img src="/images/shared/loading.gif" alt="loading" />
                                </LoadingWrapper>
                            ) : historyPopup.data?.length > 0 ? (
                                <HistoryTable>
                                    <thead>
                                        <tr>
                                            {historyPopup.data[0].map((cell, i) => (
                                                <th key={i}>{cell}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyPopup.data.slice(1).map((row, idx) => (
                                            <tr key={idx}>
                                                {row.map((cell, i) => (
                                                    <td key={i}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </HistoryTable>
                            ) : (
                                <p>No data</p>
                            )}
                        </HistoryContent>
                    </HistoryPopup>
                )}

                {enteringGame && (
                    <LoadingOverlay>
                        <img src="/images/shared/loading.gif" alt="loading" />
                    </LoadingOverlay>
                )}
            </ModalContent>
        </ModalOverlay>
    );

    // Use portal to render at document body level, avoiding z-index issues with sidebar
    return createPortal(modalContent, document.body);
}

// Styled Components
const RegionTabs = styled.div`
    display: flex;
    gap: 8px;
    padding: 10px 16px 0;
`;

const RegionTab = styled.button`
    border: ${({ $active, $color }) => ($active ? `2px solid ${$color}` : '1px solid #ccc')};
    background: ${({ $active, $color }) => ($active ? `${$color}18` : '#fff')};
    color: ${({ $color }) => $color};
    font-weight: 700;
    border-radius: 6px;
    padding: 6px 12px;
    cursor: pointer;
`;

const NoSeats = styled.p`
    text-align: center;
    padding: 40px 0;
    color: #666;
`;

const ConfirmOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: inherit;
`;

const ConfirmBox = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 20px 28px;
    min-width: 240px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .confirm-title {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
    }
    .confirm-seat {
        margin: 0;
        font-size: 22px;
        font-weight: 700;
    }
    .confirm-game {
        margin: 0;
        font-size: 14px;
        color: #666;
        word-break: break-word;
    }
    .confirm-btns {
        display: flex;
        gap: 12px;
        margin-top: 10px;
        > button {
            flex: 1;
            font: inherit;
            font-weight: 700;
            border-radius: 6px;
            padding: 8px 0;
            cursor: pointer;
        }
        > .yes {
            background: #1e6fd9;
            color: #fff;
            border: none;
        }
        > .no {
            background: #fff;
            color: #333;
            border: 1px solid #ccc;
        }
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    position: relative;
    background: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 18px;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #666;

    .balance-label {
        color: #666;
    }

    .balance {
        font-weight: 600;
        color: ${({ theme }) => theme.primary || '#1890ff'};
        margin-right: 8px;
    }

    .username {
        color: #333;
    }
`;

const CloseIcon = styled.div`
    cursor: pointer;
    mask-image: url('/images/shared/close.svg');
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    min-width: 18px;
    min-height: 18px;
    background: black;

    &:hover {
        background: #333;
    }
`;

const ModalBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    /* 換區載入中 spinner 取代表格時維持高度，避免彈窗縮小跳動
       （快速連點 tab 會誤點到遮罩外的 banner 把彈窗關掉） */
    min-height: 380px;
`;

const ActionError = styled.p`
    margin: 0 0 8px;
    text-align: center;
    color: #d93025;
    font-size: 13px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;

    img {
        width: 40px;
        height: 40px;
    }
`;

const SeatTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 8px;
        text-align: left;
        /* 格線跟隨區色（半透明），無區色時維持原樣 */
        border-bottom: 1px solid ${({ $color }) => ($color ? `${$color}55` : '#eee')};
        /* 文字跟隨區色，幫助辨識目前在哪一區；底色不動 */
        color: ${({ $color }) => $color || 'inherit'};
    }

    th {
        background: #f5f5f5;
        font-weight: 600;
        font-size: 14px;
    }

    td {
        font-size: 13px;
    }

    /* No 欄：座位號可能是 1-1 / 123-12，需保證單行並縮到內容寬度 */
    th:nth-child(2),
    td:nth-child(2) {
        white-space: nowrap;
        width: 1%;
    }
`;

const SeatRow = styled.tr`
    cursor: pointer;
    background: ${({ $isEmpty, $isMyself }) =>
        $isMyself ? '#fff3e0' : ($isEmpty ? '#f0fff0' : '#fff')};
    /* 自己的座位：粗體強調（顏色跟隨區色，由 SeatTable 控制） */
    ${({ $isMyself }) =>
        $isMyself &&
        `
        td {
            font-weight: 700;
        }
    `}

    &:hover {
        background: ${({ $isEmpty, $isMyself }) =>
            $isMyself ? '#ffe0b2' : ($isEmpty ? '#e0ffe0' : '#f9f9f9')};
    }

    .action-cell {
        width: 60px;
        text-align: center;
    }
`;

const OccupiedRow = styled.tr`
    background: #fff1f0;

    td {
        padding: 8px;
        text-align: center;
        color: #cf1322;
        font-size: 13px;
        font-weight: 600;
        border-bottom: 1px solid #eee;
    }
`;

const ExitButton = styled.button`
    background: #ff4d4f;
    color: #fff;
    border: none;
    border-radius: 6px;
    width: 48px;
    height: 48px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background: #ff7875;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

// Exit icon - door with arrow pointing out
const ExitIcon = () => (
    <svg width="36" height="36" viewBox="0 0 20 20" fill="currentColor">
        <path d="M13 3v2h2v10h-2v2h4V3h-4zm0 8V9H5.4l4.3-4.3-1.4-1.4L1.6 10l6.7 6.7 1.4-1.4L5.4 11H13z"/>
    </svg>
);

const HistoryLink = styled.span`
    color: #1890ff;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: #40a9ff;
    }
`;

const Pagination = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 0 0;
`;

const PageButton = styled.button`
    background: ${({ theme }) => theme.primary || '#1890ff'};
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const PageInfo = styled.span`
    padding: 0 12px;
    font-size: 14px;
`;

const HistoryPopup = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
`;

const HistoryContent = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    min-width: 250px;
    max-width: 90%;
`;

const HistoryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    span {
        font-weight: 600;
    }
`;

const HistoryTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 8px;
        border-bottom: 1px solid #eee;
        font-size: 13px;
    }

    th {
        font-weight: 600;
        background: #f5f5f5;
        text-align: left;
    }
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    img {
        width: 40px;
        height: 40px;
    }
`;
